import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Target } from "lucide-react";
import { api, fetchConfig, formatApiError } from "@/lib/api";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { PlaybookView } from "@/components/PlaybookView";
import { BoardView } from "@/components/board/BoardView";
import { BoardBanners } from "@/components/board/TodayBanner";
import { Segmented } from "@/components/controls/Segmented";

const TABS = [{ value: "board", label: "Schedule & news" }, { value: "playbook", label: "Playbook" }];

const CodeForm = ({ initial = "", error }) => {
  const [code, setCode] = useState(initial);
  const navigate = useNavigate();
  return (
    <div className="max-w-md mx-auto pt-4 sm:pt-10">
      <form className="card space-y-4 !p-6" onSubmit={(e) => { e.preventDefault(); navigate(`/t/${code.trim().toUpperCase()}`); }}>
        <h1 className="font-heading text-4xl font-extrabold uppercase tracking-tight">Join your team</h1>
        <p className="text-sm font-medium text-[var(--muted)]">Enter the 6-character code from your coach. No account needed.</p>
        <input data-testid="join-code-input" className="field-input w-full font-mono text-2xl tracking-[0.3em] uppercase text-center" maxLength={6} required
          value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABC123" />
        {error && <p className="text-sm font-bold text-red-600" data-testid="join-error">{error}</p>}
        <button data-testid="join-submit-button" className="btn-primary w-full">Open playbook</button>
      </form>
    </div>
  );
};

export default function JoinPage() {
  const { code } = useParams();
  const [config, setConfig] = useState(null);
  const [team, setTeam] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("board");

  useEffect(() => { fetchConfig().then(setConfig); }, []);
  useEffect(() => {
    setTeam(null);
    setError("");
    if (code) api.join(code).then(setTeam).catch((e) => setError(e.response?.status === 402 ? formatApiError(e) : "No team found for that code. Check with your coach."));
  }, [code]);
  const loadPlays = useCallback((s) => api.joinPlays(code, s), [code]);

  if (!code || error) return <PageShell><AppHeader /><CodeForm initial={code || ""} error={error} /></PageShell>;
  if (!config || !team) return <div className="min-h-screen grid place-items-center font-heading text-2xl">Loading playbook...</div>;
  const cfg = config.sports[team.sport][team.level];

  return (
    <PageShell>
      <AppHeader />
      <div className="team-banner" data-testid="join-team-banner">
        <div className="min-w-0">
          <p className="ctl-label">Team playbook · {cfg.name}{team.season ? ` · ${team.season}` : ""}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight truncate" data-testid="join-team-name">{team.name}</h1>
        </div>
        <Link to={`/t/${code}/practice`} className="btn-primary" data-testid="start-practice-link"><Target size={18} /> Practice</Link>
      </div>
      <BoardBanners code={code} />
      <Segmented options={TABS} value={tab} onChange={setTab} testPrefix="join-tab" />
      {tab === "board" ? <BoardView code={code} teamName={team.name} /> : <PlaybookView cfg={cfg} loadPlays={loadPlays} />}
    </PageShell>
  );
}
